import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["item", "input", "form", "palette", "colorInput", "emojiInput", "search", "wheelA", "wheelB", "preview"];
  static values = { code: String };

  declare readonly itemTarget: HTMLElement;
  declare readonly itemTargets: HTMLElement[];
  declare readonly inputTarget: HTMLInputElement;
  declare readonly hasInputTarget: boolean;
  declare readonly hasFormTarget: boolean;
  declare readonly formTarget: HTMLElement;
  declare readonly colorInputTarget: HTMLInputElement;
  declare readonly emojiInputTarget: HTMLInputElement;
  declare readonly searchTarget: HTMLInputElement;
  declare readonly hasSearchTarget: boolean;
  declare readonly hasColorInputTarget: boolean;
  declare readonly wheelATarget: HTMLInputElement;
  declare readonly wheelBTarget: HTMLInputElement;
  declare readonly hasWheelATarget: boolean;
  declare readonly hasWheelBTarget: boolean;
  declare readonly previewTarget: HTMLElement;
  declare readonly hasPreviewTarget: boolean;

  connect() {
    if (this.itemTargets.length > 0) {
      const firstMood = this.itemTargets[0].dataset.moodListMoodParam;
      if (firstMood) {
        this.inputTarget.value = firstMood;
        const globalColors = document.getElementById("colors") as HTMLInputElement;
        if (globalColors) globalColors.value = firstMood;
      }
      this.activateButton(this.itemTargets[0]);
    }
  }

  select(event: Event & { params?: { mood: string } }) {
    const clickedItem = event.currentTarget as HTMLElement;
    const mood = clickedItem.dataset.moodListMoodParam;
    if (!mood) return;

    this.inputTarget.value = mood;
    const globalColors = document.getElementById("colors") as HTMLInputElement;
    if (globalColors) globalColors.value = mood;

    this.activateButton(clickedItem);

  }

  activateButton(selectedItem: HTMLElement) {
    this.itemTargets.forEach((item) => {
      item.classList.remove("active");
      item.setAttribute("aria-pressed", "false");
    });
    selectedItem.classList.add("active");
    selectedItem.setAttribute("aria-pressed", "true");
  }

  openForm() {
    if (!this.hasFormTarget) return;
    const hidden = this.formTarget.hasAttribute("hidden");
    if (hidden) {
      this.formTarget.removeAttribute("hidden");
      this.emojiInputTarget?.focus();
    } else {
      this.formTarget.setAttribute("hidden", "");
    }
  }

  filter() {
    if (!this.hasSearchTarget) return;
    const q = this.searchTarget.value.toLowerCase().trim();
    this.itemTargets.forEach((item) => {
      const text = item.textContent?.toLowerCase() ?? "";
      const mood = (item.dataset.moodListMoodParam ?? "").toLowerCase();
      item.style.display = !q || text.includes(q) || mood.includes(q) ? "" : "none";
    });

    // hide category headers whose every sibling item is hidden
    this.element.querySelectorAll<HTMLElement>(".aside__category").forEach((cat) => {
      let next = cat.nextElementSibling as HTMLElement | null;
      let hasVisible = false;
      while (next && !next.classList.contains("aside__category")) {
        if (next.style.display !== "none") hasVisible = true;
        next = next.nextElementSibling as HTMLElement | null;
      }
      cat.style.display = hasVisible ? "" : "none";
    });
  }

  pickColor(event: Event) {
    const color = (event.currentTarget as HTMLElement).dataset.moodListColorParam ?? "#A8DADC";
    // A preset is a single solid colour: sync both wheels to it and drop any mix.
    if (this.hasWheelATarget) this.wheelATarget.value = color;
    if (this.hasWheelBTarget) this.wheelBTarget.value = color;
    this.setColor(color, color);
    if (this.hasFormTarget) {
      // Highlight selected swatch
      this.formTarget.querySelectorAll<HTMLElement>(".palette-swatch").forEach((s) => {
        s.classList.toggle("active", s.dataset.moodListColorParam === color);
      });
    }
  }

  // mixColors runs when either wheel changes. Two different colours produce a
  // mix ("#a|#b"); identical colours collapse to a single solid value.
  mixColors() {
    if (!this.hasWheelATarget || !this.hasWheelBTarget) return;
    this.setColor(this.wheelATarget.value, this.wheelBTarget.value);
    // A free colour choice means no preset is selected anymore.
    if (this.hasFormTarget) {
      this.formTarget.querySelectorAll<HTMLElement>(".palette-swatch").forEach((s) => {
        s.classList.remove("active");
      });
    }
  }

  // setColor writes the submitted colour — solid, or "#a|#b" when the two
  // wheels differ — and mirrors it in the live preview swatch.
  private setColor(a: string, b: string) {
    const mixed = a.toLowerCase() !== b.toLowerCase();
    if (this.hasColorInputTarget) this.colorInputTarget.value = mixed ? `${a}|${b}` : a;
    if (this.hasPreviewTarget) {
      this.previewTarget.style.background = mixed
        ? `linear-gradient(135deg, ${a} 50%, ${b} 50%)`
        : a;
    }
  }

}
